import {
    dateAdd,
    type Department,
    hour,
    type TimeEntry,
    toDateString,
    toTimeString,
    type Venue,
} from "@pentacode/core";
import { css, html, LitElement, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "../../../components/avatar/avatar";
import { app, router } from "../../../init";
import { shared } from "../../../styles";

@customElement("ptc-roster-gantt-chart")
export class RosterGanttChart extends LitElement {
    @property({ attribute: false })
    timeEntries: TimeEntry[] = [];

    @state()
    private _collapsedVenues = new Set<number>();

    @state()
    private _collapsedDepartments = new Set<number>();

    private _toggleDepartment(departmentId: number) {
        if (this._collapsedDepartments.has(departmentId)) {
            this._collapsedDepartments.delete(departmentId);
        } else {
            this._collapsedDepartments.add(departmentId);
        }
        this.requestUpdate();
    }

    private _toggleVenue(venueId: number) {
        if (this._collapsedVenues.has(venueId)) {
            this._collapsedVenues.delete(venueId);
        } else {
            this._collapsedVenues.add(venueId);
        }
        this.requestUpdate();
    }

    static styles = [
        shared,
        css`
            :host {
                display: block;
                position: relative;
                overflow: auto;
            }

            .time-grid {
                padding: 0 2em;
            }

            .time-grid-header {
                padding: 0.25em 2em;
            }

            .lanes {
                padding: 0.2em 2em;
                gap: 0.2em;
            }

            .lane {
                height: 1.6em;
                position: relative;
            }

            .entry-wrapper {
                position: absolute;
                padding: 0 0.1em 0 0.2em;
                box-sizing: border-box;
            }

            .entry {
                overflow: visible;
                font-size: 0.7em;
            }

            [part="shift"] {
                user-select: none;
                cursor: pointer;
                transition: 0.2s ease-in-out;
                transition-property: background-color, color;
                background-color: var(--color-bg-light);

                &:hover {
                    background-color: var(--color-highlight);
                    color: var(--color-bg-light);

                    [part="shift-time"] {
                        background-color: var(--color-highlight);
                    }
                }

                [part="shift-time"] {
                    cursor: pointer;
                    transition: 0.2s ease-in-out;
                    transition-property: background-color;
                }
            }
        `,
    ];

    private _renderDepartment(department: Department, timeEntries: TimeEntry[], startTime: number, endTime: number) {
        const departmentTimeEntries = timeEntries.filter((entry) => entry.position?.departmentId === department.id);
        const fullTimeSpan = endTime - startTime;

        const lanes = Object.values(Object.groupBy(departmentTimeEntries, (entry) => entry.employeeId || ""))
            .map((lane: TimeEntry[]) => {
                // sort each lane
                return lane.sort((a, b) => a.start.getTime() - b.start.getTime());
            })
            // sort lanes by start time of the first entry
            .sort((laneA, laneB) => laneA[0].start.getTime() - laneB[0].start.getTime());

        if (!lanes.length) {
            return;
        }

        const collapsed = this._collapsedDepartments.has(department.id);

        return html`
            <div>
                <div
                    class="border-bottom background center-aligning horizontal layout colored-text sticky-top click"
                    style="top: 3.6em; z-index: 2; justify-content: space-between;--color-highlight: ${department.color}; "
                    @click=${() => this._toggleDepartment(department.id)}
                >
                    <div class="smaller semibold half-padded sticky-left" style="left: 0.5em;">
                        <ptc-ui-icon name="icon.object.department"></ptc-ui-icon> ${department.name}
                    </div>
                    <div style="position: sticky; right: 0.5em;">
                        <span class="smaller semibold"
                            ><ptc-ui-icon name="icon.object.time-history"></ptc-ui-icon
                            >${departmentTimeEntries.length}</span
                        >
                        ${collapsed
                            ? html`<ptc-ui-icon name="icon.toggle.expand"></ptc-ui-icon>`
                            : html`<ptc-ui-icon name="icon.toggle.expand-active"></ptc-ui-icon>`}
                    </div>
                </div>
                <div class="border-bottom vertical layout lanes" ?hidden=${collapsed}>
                    ${lanes.map(
                        (lane) => html`
                            <div class="lane">
                                ${lane.map((entry) => {
                                    const employee = app.getEmployee(entry.employeeId);
                                    if (!employee || !entry.position) {
                                        return nothing;
                                    }
                                    // Capture: entry.position is a getter, not narrowed inside the closure below.
                                    const position = entry.position;
                                    const left = ((entry.start.getTime() - startTime) / fullTimeSpan) * 100;
                                    const width = ((entry.end.getTime() - entry.start.getTime()) / fullTimeSpan) * 100;
                                    const shortShift = entry.end.getTime() - entry.start.getTime() < 3 * hour;
                                    return html`
                                        <div class="entry-wrapper" style="left: ${left}%; width: ${width}%;">
                                            <div
                                                part="shift"
                                                @click=${(ev: PointerEvent) => {
                                                    if (ev.shiftKey) {
                                                        router.go(`employees/${employee.id}/time`, {
                                                            from: toDateString(entry.start),
                                                            to: dateAdd(toDateString(entry.start), { months: 1 }),
                                                        });
                                                    } else {
                                                        router.go(`roster`, {
                                                            from: dateAdd(toDateString(entry.start), { days: -3 }),
                                                            to: dateAdd(toDateString(entry.start), { days: 4 }),
                                                            employee: employee.id.toString(),
                                                            department: position.departmentId.toString(),
                                                        });
                                                    }
                                                }}
                                                class="box center-aligning spacing horizontal layout background entry"
                                                style="justify-content: space-between; --color-highlight: ${app.getPositionColor(
                                                    entry.position
                                                )};"
                                            >
                                                <div
                                                    class="half-padded center-aligning spacing horizontal layout sticky-left fit-horizontally rounded"
                                                >
                                                    <ptc-avatar .employee=${employee} class="tinier"></ptc-avatar>
                                                    <div class="stretch ellipsis">
                                                        <span class="semibold"
                                                            >${employee.firstName.slice(0, 1)}.
                                                            ${employee.lastName}</span
                                                        >
                                                        - ${entry.position.name}
                                                    </div>
                                                </div>
                                                <div
                                                    part="shift-time"
                                                    ?hidden=${shortShift}
                                                    class="half-padded bold background rounded"
                                                    style="position: sticky; right: 0.5em; z-index: 1;"
                                                >
                                                    ${toTimeString(entry.start)} - ${toTimeString(entry.end)}
                                                </div>
                                            </div>
                                        </div>
                                    `;
                                })}
                            </div>
                        `
                    )}
                </div>
            </div>
        `;
    }

    private _renderVenue(venue: Venue, timeEntries: TimeEntry[], startTime: number, latestTime: number) {
        const venueTimeEntries = timeEntries.filter((entry) => {
            if (!entry.positionId) return false;
            const position = app.getPosition(entry.positionId);
            return position?.venue?.id === venue.id;
        });
        if (!venueTimeEntries.length) {
            return;
        }
        const collapsed = this._collapsedVenues.has(venue.id);
        return html`
            <div>
                <div
                    class="border-bottom background start-aligning horizontal layout sticky-top click"
                    style="top: 2em; z-index: 3; justify-content: space-between;"
                    @click=${() => this._toggleVenue(venue.id)}
                >
                    <div class="smaller semibold half-padded sticky-left">
                        <ptc-ui-icon name="icon.object.venue"></ptc-ui-icon> ${venue.name}
                    </div>
                    <div style="position: sticky; right: 0.5em;">
                        <span class="smaller semibold"
                            ><ptc-ui-icon name="icon.object.time-history"></ptc-ui-icon>${venueTimeEntries.length}</span
                        >
                        ${collapsed
                            ? html`<ptc-ui-icon name="icon.toggle.expand"></ptc-ui-icon>`
                            : html`<ptc-ui-icon name="icon.toggle.expand-active"></ptc-ui-icon>`}
                    </div>
                </div>
                <div ?hidden=${collapsed}>
                    ${venue.departments
                        .filter((department) => app.hasAccess({ department }))
                        .map((department) =>
                            this._renderDepartment(department, venueTimeEntries, startTime, latestTime)
                        )}
                </div>
            </div>
        `;
    }

    private _getHoursArray(startTime: number, endTime: number): number[] {
        const hours: number[] = [];
        for (let time = startTime; time < endTime; time += hour) {
            hours.push(time);
        }

        return hours;
    }

    private _renderTimeGrid(startTime: number, endTime: number) {
        const hours = this._getHoursArray(startTime, endTime);

        return html`
            <div class="fullbleed vertical layout time-grid">
                <div class="evenly stretching horizontal layout stretch">
                    ${hours.map(
                        () => html`
                            <div class="stretch border-left"></div>
                            <div class="stretch border-left" style="border-style: dashed;"></div>
                        `
                    )}
                </div>
            </div>
        `;
    }

    render() {
        const timeEntries = this.timeEntries.filter((entry) => entry.startPlanned && entry.endPlanned);
        if (timeEntries.length === 0) {
            return html`<div class="double-padded text-centering subtle place-self-center">
                <span class="emoji huger">⛱️</span>
                <p>Nichts für heute geplant – der Dienstplan gönnt sich eine Pause</p>
            </div>`;
        }

        // This will throw if timeEntries is empty, but we already check for that above.
        // The non-null assertion is safe because we filter for startPlanned above.
        const earliestTime = Math.min(...timeEntries.map((entry) => entry.startPlanned!.getTime()));
        const latestTime = Math.max(...timeEntries.map((entry) => entry.endPlanned!.getTime()));
        // Round down the start time to the nearest full hour.
        const startTime = earliestTime - (earliestTime % hour);
        // Round up the end time to the nearest full hour.
        const endTime = latestTime % hour === 0 ? latestTime : latestTime - (latestTime % hour) + hour;

        if (endTime <= startTime) {
            return html`<div class="error-state">Ungültiger Zeitbereich</div>`;
        }

        const fullTimeSpanInHours = (endTime - startTime) / hour;
        const hours = this._getHoursArray(startTime, endTime);

        return html`
            <div style="position: relative; min-width: ${fullTimeSpanInHours * 4}em">
                ${this._renderTimeGrid(startTime, endTime)}
                <div style="position: relative; z-index: 1;">
                    <div
                        class="background evenly stretching horizontal layout border-bottom sticky-top time-grid-header"
                        style="z-index: 4;"
                    >
                        ${hours.map(
                            (time) =>
                                html` <div>
                                    <div
                                        class="smaller semibold centering horizontal layout"
                                        style="transform: translateX(-50%);"
                                    >
                                        <div class="half-padded background rounded">
                                            ${toTimeString(new Date(time))}
                                        </div>
                                    </div>
                                </div>`
                        )}
                    </div>
                    ${app.accessibleVenues.map((venue) => this._renderVenue(venue, timeEntries, startTime, endTime))}
                </div>
            </div>
        `;
    }
}
